import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isProblem } from '@rooming/web-core';
import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { backoffice, type Me, type SessionStage, type StaffRole } from './api';

// Session of the backoffice, always read from the server (the cookie is HttpOnly). `me` exists only for a verified
// session; what the user may do is decided by the API — the UI only hides what would be refused anyway.

interface SessionValue {
  readonly loading: boolean;
  readonly stage: SessionStage | 'ANONYMOUS';
  readonly me: Me | undefined;
  readonly setStage: (stage: SessionStage | 'ANONYMOUS') => void;
  readonly signOut: () => Promise<void>;
  readonly hasRole: (...roles: StaffRole[]) => boolean;
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ['session'],
    queryFn: async (): Promise<SessionStage | 'ANONYMOUS'> => {
      try {
        return (await backoffice.session()).stage;
      } catch (error) {
        if (isProblem(error) && error.status === 401) return 'ANONYMOUS';
        throw error;
      }
    },
    staleTime: 60_000,
  });
  const stage = session.data ?? 'ANONYMOUS';
  const me = useQuery({ queryKey: ['me'], queryFn: backoffice.me, enabled: stage === 'ACTIVE', staleTime: 60_000 });

  const setStage = useCallback(
    (next: SessionStage | 'ANONYMOUS') => {
      queryClient.setQueryData(['session'], next);
    },
    [queryClient],
  );
  const signOut = useCallback(async () => {
    try {
      await backoffice.logout();
    } catch {
      // the session may already be gone; the local state is cleared anyway
    }
    // Nothing of the previous user may survive in memory.
    queryClient.setQueryData(['session'], 'ANONYMOUS');
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
  }, [queryClient]);
  const roles = me.data?.roles;
  const hasRole = useCallback(
    (...wanted: StaffRole[]) => wanted.some((role) => roles?.includes(role) === true),
    [roles],
  );

  return (
    <SessionContext.Provider
      value={{
        loading: session.isPending || (stage === 'ACTIVE' && me.isPending),
        stage,
        me: me.data,
        setStage,
        signOut,
        hasRole,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === undefined) throw new Error('useSession outside SessionProvider');
  return value;
}
